// TODO: migrate from legacy CI tooling: resolve ts-* and/or eslint-* directives below (including inline comments)
/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-return */

import moment from "moment";
import React, { useState } from "react";
import { TrialLogData } from "../../../../apis/types/trialTypes";
import { useModuleNavigation } from "../../../../hooks/useModuleNavigation";
import { useUserAuth } from "../../../../hooks/useUserAuth.js";
import { useUserSettings } from "../../../../hooks/useUserSettings.js";
import { ApplicationArea } from "../../../Lumber/ApplicationAreas";
import PubUnpubTrialFlightsFromSearch from "../../../Lumber/PubUnpubTrialFlights/PubUnpubTrialFlightsFromSearch.js";
import ResultsTable from "../../../Lumber/Search/ResultsTable";
import { AnyFilter } from "../../../Lumber/Search/types";
import TrialFavoritingWidget from "../../Widgets/Favorites/TrialFavoritingWidget.js";

interface Props {
	loading: boolean;
	data: TrialLogData[];
	filters: { [key in keyof TrialLogData]?: AnyFilter };
	searchTerm: { term: string; cols: (keyof TrialLogData)[] };
	openMetaTagsModal: (trialId: any, trialName: any, open?: boolean) => void;
}

export const TrialSearchResults = ({ data, loading, filters, searchTerm, openMetaTagsModal }: Props) => {
	const DATE_FORMAT = "MM/DD/YYYY h:mma";
	const moduleNavigation = useModuleNavigation();
	const userSettings = useUserSettings();
	const userAuth = useUserAuth();

	const [modalOpen, setModalOpen] = useState(false);
	const [modalTrialId, setModalTrialId] = useState("");

	const headers = {
		trialName: "Trial",
		clientName: "Client",
		cooperatorName: "Cooperator",
		location: "Location",
		owner: "Owner",
		companyName: "Company",
		growingSeasonName: "Growing Season",
		farmAbbr: "Farm",
		fieldName: "Field",
		cropName: "Crop",
		plantDate: "Plant Date",
		statuses: "Status",
	};

	return (
		<>
			<ResultsTable<TrialLogData>
				level="trial"
				loading={loading}
				data={data}
				headers={headers}
				onRenderDataColumn={{
					plantDate: (pd) => (pd ? moment(pd).format(DATE_FORMAT) : undefined),
					location: ({ city, region, countryCode }) => `${city}, ${region}, ${countryCode}`,
					statuses: (s) => [
						{
							icon: "clipboard",
							color:
								s.groundDataStatus === "Collected" ? "green" : s.groundDataStatus === "Planned" ? "yellow" : "grey",
							title: `Ground Data: ${s.groundDataStatus}`,
							enabled: true,
						},
						{
							icon: "line graph increase",
							color: s.growthStatus ? "green" : "grey",
							title: `Growth Curves: ${s.growthStatus ? "Ran" : "Not Run"}`,
							enabled: true,
						},
						{
							icon: "line graph decrease",
							color: s.senescenceStatus ? "green" : "grey",
							title: `Senescence Curves: ${s.senescenceStatus ? "Ran" : "Not Run"}`,
							enabled: true,
						},
						{
							icon: "area graph",
							color: s.fullSeasonStatus ? "green" : "grey",
							title: `Full Season Curves: ${s.fullSeasonStatus ? "Ran" : "Not Run"}`,
							enabled: true,
						},
						{
							icon: s.hasExclusions ? "warning solid" : "warning",
							color: "red",
							title: `Trial Has ${s.hasExclusions ? "Full Plot" : "Partial"} Exclusions`,
							enabled: s.hasExclusions || s.hasPartialExclusions,
						},
						{
							icon: s.hasFlaggings ? "flag solid" : "flag",
							color: "yellow",
							title: `Trial Has ${s.hasFlaggings ? "Full Plot" : "Partial"} Flaggings`,
							enabled: s.hasFlaggings || s.hasPartialFlaggings,
						},
					],
				}}
				onSortColumn={{
					trialName: true,
					clientName: true,
					cooperatorName: true,
					location: (a, b) =>
						(a.country ?? "").localeCompare(b.country ?? "") ||
						(a.region ?? "").localeCompare(b.region ?? "") ||
						(a.city ?? "").localeCompare(b.city ?? ""),
					owner: true,
					companyName: true,
					growingSeasonName: true,
					farmAbbr: true,
					fieldName: true,
					cropName: true,
					plantDate: (a, b) => moment(b).diff(moment(a)),
				}}
				columnFilters={filters}
				actions={[
					{
						hasArea: (row) => userAuth.hasApplicationArea(ApplicationArea.TrialAnalysis, row.clientId),
						icon: "chart bar outline",
						title: "Analysis",
						to: (row) => moduleNavigation.createTrialLink(false, row.trialId, "analysis"),
					},
					{
						hasArea: (row) => userAuth.hasApplicationArea(ApplicationArea.TrialEdit, row.clientId),
						icon: "tags",
						title: "Meta-Tags & Annotations",
						onClick: (row) => {
							openMetaTagsModal(row.trialId, row.trialName, true);
						},
					},
					{
						hasArea: (row) => userAuth.hasApplicationArea(ApplicationArea.TrialEdit, row.clientId),
						icon: "pencil",
						title: "Edit",
						to: (row) => moduleNavigation.createTrialLink(false, row.trialId, "edit"),
					},
					{
						hasArea: (row) => userAuth.hasApplicationArea(ApplicationArea.TrialHeatMap, row.clientId),
						icon: "map",
						title: "Heatmap",
						to: (row) => moduleNavigation.createTrialLink(false, row.trialId, "heatmap"),
					},
					{
						hasArea: () => true,
						component: (row) => (
							<TrialFavoritingWidget
								key={row.trialId}
								clientId={row.clientId}
								trialId={row.trialId}
								userSettings={userSettings}
								displayMode="table"
							/>
						),
					},
					{
						hasArea: () => userAuth.isApAdmin,
						icon: "book",
						title: "Publishing",
						onClick: (row) => {
							setModalTrialId(row.trialId);
							setModalOpen(true);
						},
					},
				]}
				links={{
					trialName: (row) =>
						moduleNavigation.createTrialLink(false, row.trialId, row.fieldScaleTrial ? "geo-mfa-explorer" : "viewer"),
					farmAbbr: (row) => moduleNavigation.createFarmLink(false, row.farmId, "viewer"),
					fieldName: (row) => moduleNavigation.createFieldLink(false, row.fieldId),
				}}
				searchTerm={searchTerm}
			/>
			<PubUnpubTrialFlightsFromSearch
				trialId={modalTrialId}
				mode="menu"
				modalOpen={modalOpen}
				setModalOpen={setModalOpen}
			/>
		</>
	);
};
